import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Form, InputField } from "@mimber/forms"
import { Button, message } from "@mimber/ui-web"
import {
  SubmitInviteCodeDtoSchema,
  type SubmitInviteCodeDto,
} from "@mimber/schema"
import { useSubmitInviteCode } from "@mimber/client/mimber-organization"
import type { SubmitInviteCodeFormProps } from "./SubmitInviteCodeForm.types"

const INVITE_CODE_KEY = "mimber_invite_code"

export const SubmitInviteCodeForm: React.FunctionComponent<
  SubmitInviteCodeFormProps
> = ({ onSuccess, defaultInviteCode }) => {
  const navigate = useNavigate()
  const form = useForm<SubmitInviteCodeDto>({
    resolver: zodResolver(SubmitInviteCodeDtoSchema),
    mode: "onBlur",
    defaultValues: {
      inviteCode: defaultInviteCode ?? "",
    },
  })

  const submitInviteCode = useSubmitInviteCode({
    onSuccess: () => {
      message.success("Invite code accepted! Welcome to Mimber.")
      localStorage.removeItem(INVITE_CODE_KEY)
      // Redeeming a code is a one-time event, so send the user straight to
      // the waitlist welcome page once the parent re-checks auth
      navigate({ to: "/waitlist-welcome" })
      onSuccess?.()
    },
    onError: (error) => {
      localStorage.removeItem(INVITE_CODE_KEY)

      if (error.name === "RateLimitError") {
        message.error(error.message)
        return
      }

      const errorMessage =
        error?.response?.data?.meta?.error?.message ||
        error?.message ||
        "Invalid or expired invite code. Please try again."

      message.error(errorMessage)
    },
  })

  const handleSubmit = (data: SubmitInviteCodeDto) => {
    submitInviteCode.mutate(data)
  }

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <InputField
        name="inviteCode"
        type="text"
        label="Invite Code"
        placeholder="Enter your invite code"
        required
      />
      <Button
        type="primary"
        htmlType="submit"
        loading={submitInviteCode.isPending}
        className="w-full"
      >
        Submit Invite Code
      </Button>
    </Form>
  )
}
